import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { serviceService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { Star, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function PublicFeedback() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    serviceService.publicInfo(token)
      .then((d) => { setInfo(d); if (d.alreadySubmitted) setDone(true); })
      .catch((e) => setError(apiErrorMessage(e.response?.data?.detail) || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try { await serviceService.publicSubmit(token, { rating, comment: comment || undefined }); setDone(true); }
    catch (e) { setError(apiErrorMessage(e.response?.data?.detail) || "Could not submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="public-feedback-page">
      <div className="w-full max-w-md bg-slate-900 rounded-2xl border border-slate-800 p-8 shadow-2xl">
        <div className="text-center mb-6">
          <div className="inline-flex h-12 w-12 rounded-2xl bg-teal-500/15 text-teal-400 items-center justify-center mb-3"><Star className="h-6 w-6" /></div>
          <h1 className="text-xl font-bold text-white">How was our support?</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
        ) : error ? (
          <div className="text-center py-8" data-testid="public-feedback-error">
            <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
            <p className="text-slate-300">{error}</p>
          </div>
        ) : done ? (
          <div className="text-center py-8" data-testid="public-feedback-thanks">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-semibold text-lg">Thank you for your feedback!</p>
            <p className="text-slate-400 text-sm mt-1">Ticket {info?.ticketNumber}</p>
          </div>
        ) : (
          <div>
            <p className="text-slate-400 text-sm text-center mb-5">Regarding ticket <span className="text-slate-200 font-medium">{info?.ticketNumber}</span> — {info?.subject}</p>
            <div className="flex justify-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)} onClick={() => setRating(i)}
                  className="transition-transform hover:scale-110" data-testid={`public-feedback-star-${i}`}>
                  <Star className={`h-9 w-9 ${i <= (hover || rating) ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} />
                </button>
              ))}
            </div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Tell us more (optional)…"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-teal-500 mb-4" data-testid="public-feedback-comment" />
            <button onClick={submit} disabled={!rating || submitting}
              className="w-full bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-lg py-2.5 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors" data-testid="public-feedback-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit feedback
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
